import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <Logo size="md" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose dark:prose-invert max-w-none">
          <h1>Privacy Policy</h1>
          <p className="text-muted-foreground italic">
            Last Updated: February 24, 2026
          </p>

          <section className="mt-8">
            <h2>1. Introduction</h2>
            <p>
              Welcome to DailyHisaab ("we," "our," or "us"), operated by ASL
              Digital Solutions. We are committed to protecting your privacy and
              handling your personal data responsibly. This Privacy Policy
              explains what information we collect, how we use it, how we store
              and protect it, and your rights regarding your data when you use
              the DailyHisaab application ("App") available at dailyhisaab.com
              and through mobile platforms.
            </p>
            <p>
              By using DailyHisaab, you consent to the data practices described
              in this policy. If you do not agree, please do not use the App.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Account Information (via Google Sign-In)</h3>
            <p>
              When you sign in using your Google account, we receive and store
              the following:
            </p>
            <ul>
              <li>
                <strong>Email address</strong> — used to identify your account
              </li>
              <li>
                <strong>Display name</strong> — used to personalize your
                experience
              </li>
              <li>
                <strong>Google user ID (UID)</strong> — used as a unique account
                identifier
              </li>
            </ul>
            <p>
              We do <strong>not</strong> collect or store your Google password.
              Authentication is handled securely by Google's OAuth 2.0 system
              via Firebase Authentication.
            </p>
            <h3>2.2 Customer Information:</h3>
            <p>
              When you use our app to generate invoices, you may input
              third-party data such as your customers' phone numbers. We collect
              and temporarily store this data solely for the purpose of
              facilitating digital invoicing (e.g., sharing receipts via
              WhatsApp) and store management features (e.g., syncing with your
              Google Sheets). We do not sell, rent, or use your customers' phone
              numbers for our own marketing purposes.
            </p>

            <h3>2.3 Business Information (User-Provided)</h3>
            <p>
              You may voluntarily provide the following business details within
              the App:
            </p>
            <ul>
              <li>Business name, address, phone number, email, and website</li>
              <li>Owner/proprietor name</li>
              <li>Business logo (uploaded by you)</li>
              <li>GST Identification Number (GSTIN), CIN, and FSSAI number</li>
              <li>GST preferences (enabled, inclusive, rate)</li>
            </ul>

            <h3>2.4 Product and Transaction Data</h3>
            <p>
              The core function of DailyHisaab is to help you manage your
              business billing and inventory. We process and store:
            </p>
            <ul>
              <li>
                <strong>Products:</strong> Name, sale price, cost price,
                category, stock quantity, low stock alerts, HSN code, GST rate,
                barcode, and product type
              </li>
              <li>
                <strong>Transactions:</strong> Sales and expense records
                including product name, quantity, rate, total amount, type
                (sale/expense), and timestamp
              </li>
            </ul>

            <h3>2.5 Device Permissions</h3>
            <ul>
              <li>
                <strong>Camera:</strong> Used solely for barcode scanning to
                quickly add or look up products. Camera data is processed in
                real-time on your device and is not transmitted to our servers
                or stored.
              </li>
              <li>
                <strong>Bluetooth & Nearby Devices:</strong> Used strictly to
                find, connect to, and determine the relative position of nearby
                Bluetooth thermal printers. This permission is essential for
                printing physical receipts for your customers directly from the
                App. We do not use this permission to track your location or
                collect personal data.
              </li>
            </ul>

            <h3>2.6 Locally Stored Data</h3>
            <p>
              The App uses your device's local storage mechanisms for
              functionality:
            </p>
            <ul>
              <li>
                <strong>localStorage:</strong> Stores your Google access token
                (for Sheets API), theme preference, low stock alert settings,
                and cached barcode product data
              </li>
              <li>
                <strong>IndexedDB:</strong> Caches your products and
                transactions locally for offline access and faster loading
              </li>
            </ul>

            <h3>2.7 App Usage Data</h3>
            <ul>
              <li>Reward points balance and history</li>
              <li>User mode preference (Normal or Pro)</li>
              <li>Pro mode expiry dates</li>
              <li>Sync status and preferences</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>
                <strong>Authentication:</strong> To verify your identity and
                provide secure access to your account
              </li>
              <li>
                <strong>Core App Functionality:</strong> To manage your
                products, record transactions, generate invoices, and display
                dashboard analytics
              </li>
              <li>
                <strong>Google Sheets Sync:</strong> To sync your products and
                transactions to your personal Google Sheets for backup and
                reporting
              </li>
              <li>
                <strong>Barcode Product Lookup:</strong> To search product
                databases when you scan a barcode
              </li>
              <li>
                <strong>Notifications:</strong> To alert you about low stock
                levels
              </li>
              <li>
                <strong>Reward System:</strong> To track and manage your reward
                points for app features
              </li>
              <li>
                <strong>Invoice Generation:</strong> To create and share
                professional invoices using your business details
              </li>
            </ul>
          </section>

          <section>
            <h2>4. Third-Party Services</h2>
            <p>
              We use trusted third-party services to securely operate and
              improve our app. This includes{" "}
              <strong data-path-to-node="11,0,0" data-index-in-node="129">
                Firebase Authentication
              </strong>{" "}
              (to securely manage your login credentials) and{" "}
              <strong data-path-to-node="11,0,0" data-index-in-node="201">
                Google Analytics
              </strong>{" "}
              (to track crash reports and app performance so we can fix bugs).
              These services may collect anonymous usage data. You can learn
              more about how Google handles your data by visiting Google's
              Privacy Policy.
            </p>

            <h3>4.1 Google Services</h3>
            <ul>
              <li>
                <strong>Google Sheets API:</strong> Reads and writes your
                product and transaction data to spreadsheets in your Google
                Drive
              </li>
              <li>
                <strong>Google Drive API:</strong> Lists and manages DailyHisaab
                spreadsheet files in your Drive
              </li>
            </ul>
            <p>We request the following Google OAuth scopes:</p>
            <ul>
              <li>
                <code>spreadsheets</code> — to read/write your spreadsheet data
              </li>
              <li>
                <code>drive.file</code> — to access only files created by or
                opened with DailyHisaab
              </li>
            </ul>
            <p>
              We do <strong>not</strong> access any other files in your Google
              Drive.
            </p>

            <h3>4.2 Firebase (Google)</h3>
            <ul>
              <li>
                <strong>Firebase Authentication:</strong> Manages user sign-in
                securely
              </li>
              <li>
                <strong>Cloud Firestore:</strong> Stores your user profile,
                business settings, reward points, and app preferences
              </li>
            </ul>
            <p>
              Firebase's privacy policy:{" "}
              <a
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://firebase.google.com/support/privacy
              </a>
            </p>

            <h3>4.3 Open Food Facts / Open Products Facts</h3>
            <p>
              When you scan a barcode, the App may query these open-source
              product databases to retrieve product names and details. Only the
              barcode number is sent — no personal or business data is shared.
            </p>
            <ul>
              <li>
                Open Food Facts:{" "}
                <a
                  href="https://world.openfoodfacts.org/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                Open Products Facts:{" "}
                <a
                  href="https://world.openproductsfacts.org/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2>5. Data Storage and Security</h2>

            <h3>5.1 Where Your Data Is Stored</h3>
            <ul>
              <li>
                <strong>Google Sheets (your Drive):</strong> Your product and
                transaction data. You have full ownership and control.
              </li>
              <li>
                <strong>Firebase Cloud Firestore:</strong> Your user profile,
                business settings, reward points, and preferences. Hosted on
                Google Cloud infrastructure.
              </li>
              <li>
                <strong>Your Device:</strong> Cached data in IndexedDB and
                localStorage for offline access.
              </li>
            </ul>

            <h3>5.2 Security Measures</h3>
            <ul>
              <li>All data transmission uses HTTPS/TLS encryption</li>
              <li>
                Authentication is handled by Google's OAuth 2.0 and Firebase
                Auth
              </li>
              <li>
                Access tokens are stored locally and never transmitted to our
                servers
              </li>
              <li>We do not store passwords</li>
              <li>
                Firestore security rules restrict data access to authenticated
                users only
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Google User Data & Limited Use Disclosure</h2>

            <h3>6.1 Access to Google Sheets:</h3>
            <ul>
              <li>
                DailyHisaab accesses your Google Sheets to provide core app
                functionality, including inventory management and billing.
              </li>
            </ul>
            <h3>6.2 Limited Use Policy:</h3>
            <ul>
              <li>
                DailyHisaab&rsquo;s use and transfer of information received
                from Google APIs to any other app will adhere to the{" "}
                <strong data-path-to-node="4" data-index-in-node="132">
                  <a
                    class="ng-star-inserted"
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    target="_blank"
                    rel="noopener"
                    data-hveid="0"
                    data-ved="0CAAQ_4QMahgKEwjbv-yA0vGSAxUAAAAAHQAAAAAQrgE"
                  >
                    Google API Services User Data Policy
                  </a>
                </strong>
                , including the Limited Use requirements.
              </li>
            </ul>
            <h3>6.3 Storage and Transparency:</h3>
            <ul>
              <li>
                We do not store your spreadsheet data on our servers. All
                financial and inventory data remains within your personal Google
                Drive/Sheets account. We do not request access to data that is
                not essential for the app's current features.
              </li>
            </ul>
          </section>

          <section>
            <h2>7. Third-Party Advertising (AdMob)</h2>
            <ul>
              <p data-path-to-node="7">
                7.1{" "}
                <strong data-path-to-node="7" data-index-in-node="4">
                  Personalized Ads:
                </strong>{" "}
                We may use third-party vendors, including Google, which use
                cookies or device identifiers (like the Android Advertising ID)
                to serve ads based on your prior visits to this app or other
                websites.
              </p>
              <p data-path-to-node="8">
                7.2{" "}
                <strong data-path-to-node="8" data-index-in-node="4">
                  Ad Choices:
                </strong>{" "}
                You can opt-out of personalized advertising by visiting{" "}
                <strong data-path-to-node="8" data-index-in-node="72">
                  <a
                    class="ng-star-inserted"
                    href="https://www.google.com/search?q=https://www.google.com/settings/ads"
                    target="_blank"
                    rel="noopener"
                    data-hveid="0"
                    data-ved="0CAAQ_4QMahgKEwjbv-yA0vGSAxUAAAAAHQAAAAAQrwE"
                  >
                    Google Ad Settings
                  </a>
                </strong>{" "}
                or by adjusting the "Ads" settings on your Android device.
              </p>
              <p data-path-to-node="9">
                7.3{" "}
                <strong data-path-to-node="9" data-index-in-node="4">
                  Third-Party Collection:
                </strong>{" "}
                For more information on how Google uses data when you use our
                app, please visit{" "}
                <strong data-path-to-node="9" data-index-in-node="108">
                  <a
                    class="ng-star-inserted"
                    href="https://policies.google.com/technologies/partner-sites"
                    target="_blank"
                    rel="noopener"
                    data-hveid="0"
                    data-ved="0CAAQ_4QMahgKEwjbv-yA0vGSAxUAAAAAHQAAAAAQsAE"
                  >
                    How Google uses information from sites or apps that use our
                    services
                  </a>
                </strong>
                .
              </p>
            </ul>
            <p>
              You may request deletion of your account and associated data at
              any time. See our{" "}
              <Link href="/data-deletion" className="text-primary underline">
                Data Deletion Policy
              </Link>{" "}
              for details.
            </p>
          </section>

          <section>
            <h2>8. Disclosure of Firebase & SDKs</h2>
            <p data-path-to-node="11">
              8.1{" "}
              <strong data-path-to-node="11" data-index-in-node="4">
                Service Usage:
              </strong>{" "}
              We use Google Firebase SDKs for secure user authentication and app
              analytics.
            </p>
            <p data-path-to-node="12">
              8.2{" "}
              <strong data-path-to-node="12" data-index-in-node="4">
                Data Types Collected:
              </strong>{" "}
              Firebase automatically collects certain information, including
              device metadata (OS version, model), IP addresses (for security
              and abuse prevention), and app installation identifiers.
            </p>
            <p data-path-to-node="13">
              8.3{" "}
              <strong data-path-to-node="13" data-index-in-node="4">
                Data Security:
              </strong>{" "}
              All end-user data collected by Firebase SDKs is encrypted in
              transit using HTTPS to ensure it remains protected while being
              processed.
            </p>
          </section>

          <section>
            <h2>9. Account and Data Deletion Instructions</h2>
            <p data-path-to-node="15">
              9.1{" "}
              <strong data-path-to-node="15" data-index-in-node="4">
                Right to Deletion:
              </strong>{" "}
              In accordance with Google Play requirements, we provide users with
              a way to delete their app accounts and associated data.
            </p>
            <p data-path-to-node="16">
              9.2{" "}
              <strong data-path-to-node="16" data-index-in-node="4">
                How to Request Deletion:
              </strong>{" "}
              You may request account deletion at any time. To do so, please
              contact us at{" "}
              <strong data-path-to-node="16" data-index-in-node="106">
                support@dailyhisaab.com
              </strong>{" "}
              or follow the "Delete Account" link provided within the app
              settings.
            </p>
            <p data-path-to-node="17">
              9.3{" "}
              <strong data-path-to-node="17" data-index-in-node="4">
                Data Handling:
              </strong>{" "}
              Upon your request, your account and associated Firebase data will
              be removed from our active systems. Please note that since your
              billing data is stored in your own Google Sheet, you must manually
              delete the spreadsheet from your Google Drive to remove that
              specific data.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated "Last Updated" date.
              Continued use of the App after changes constitutes acceptance of
              the updated policy.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this
              Privacy Policy or your data, please contact us:
            </p>
            <ul>
              <li>
                <strong>Email:</strong> support@dailyhisaab.com
              </li>
              <li>
                <strong>Legal:</strong> contact@asldigitalsolutions.in
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a
                  href="https://dailyhisaab.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dailyhisaab.com
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mb-safe">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Link href="/">
              <Logo size="sm" />
            </Link>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                ©2026 DailyHisaab.🇮🇳 All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
